import React, { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import { Container } from 'semantic-ui-react';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';
import SliderContent from './categoryHeroBanner/SliderContent';
import SliderControls from './categoryHeroBanner/SliderControls';
import SliderPlaceholderImage from './categoryHeroBanner/SliderPlaceholderImage';
import SuperTemplateBreadcrumbs from './SuperTemplateBreadcrumbs';
import SuperTemplateQuickLinks from './SuperTemplateQuickLinks';
import Arrow from '../assets/images/cursorArrow-left.inline.svg';

const CategoryHeroBanner = (props) => {
  const [pageLoaded, setPageLoaded] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [isOffScreen, setIsOffScreen] = useState(true);
  const [arrowX, setArrowX] = useState(0);
  const [arrowY, setArrowY] = useState(0);
  const [rightArrow, setRightArrow] = useState(false);
  const [mobileUseMask, setMobileUseMask] = useState(false);
  const [sliderIntersectRef, sliderInView] = useInView({
    threshold: 0,
  });
  const isSwipe = useRef(false);
  const [playState, setPlayState] = React.useState("play")

  const updateSlide = (slide) => {
    return {
      ...slide,
      title: slide?.title,
      cta: slide?.cta,
      ctaAriaLabel: slide?.ctaAriaLabel,
      introContent: slide?.introContent,
      image: slide?.desktopImage?.mediaItemUrl,
      retinaImage: slide?.desktopRetinaImage?.mediaItemUrl,
      mobileImage: slide?.mobileImage?.mediaItemUrl,
      mobileRetinaImage: slide?.mobileRetinaImage?.mediaItemUrl,
    };
  };

  const mouseMove = (e) => {
    setIsOffScreen(false);
    const { innerWidth: width } = window;
    const halfBanner = width / 2;

    setArrowX(e.clientX - 41);
    setArrowY(e.clientY - 41);

    if (e.clientX > halfBanner) {
      setRightArrow(true);
    } else {
      setRightArrow(false);
    }
  };

  const slideClick = (e) => {
    if (isOffScreen) {
      return;
    }

    const { innerWidth: width } = window;

    if (width > 768) {
      const halfBanner = width / 2;

      if (e.clientX > halfBanner) {
        gotoNext();
      } else {
        gotoPrev();
      }
    }
  };

  // slide object
  const [slide, setSlide] = useState(updateSlide(props?.module?.slide[0]));

  // set transition timer on timer element and update animation placeholder image with current slide,
  const startTime = (currentIndex) => {
    setTime(100);
    setPlaying(true);
    // tells slider dot navigation current slide
    setSlideIndex(sliderRef?.current?.innerSlider?.state?.currentSlide);
    setPlaceholderSlide(currentIndex);
    handleContentAnimation(currentIndex);
  };

  const handleContentAnimation = (currentIndex) => {
    const currentActive = document.querySelector(
      '.category-hero-banner__content-container.current-active'
    );
    if (currentActive) {
      currentActive.classList.remove('current-active');
    }

    const newActive = document.querySelector(
      `.slick-slide[data-index="${currentIndex}"] .category-hero-banner__content-container`
    );
    if (newActive) {
      newActive.classList.add('current-active');
    }
    if (!hasSlides) {
      const content = document.querySelector('.category-hero-banner__content-container');
      if (content) {
        content.classList.add('current-active');
      }
    }
  };

  // update animation placeholder image with current slide
  const setPlaceholderSlide = (currentIndex) => {
    setSlide(updateSlide(props?.module?.slide[currentIndex]));
  };

  const restartTime = () => {
    setTime(0);
  };

  // reset transition on timer element and go to next slide
  const gotoNext = () => {
    sliderRef.current.slickNext();
    restartTime();
    setPlaying(false);
  };

  // reset transition on timer element and go to next slide
  const gotoPrev = () => {
    sliderRef.current.slickPrev();
    restartTime();
    setPlaying(false);
  };

  // reset transition on timer element and go to a selected slide
  const gotoSlide = (slideIndex) => {
    setMobileUseMask(true);
    sliderRef.current.slickGoTo(slideIndex);
    restartTime();
    setPlaying(false);
    setSlideIndex(slideIndex);
  };

  // logic to initialize timer on document load
  useEffect(() => {
    const onPageLoad = () => {
      startTime(0);
    };

    if (document.readyState === 'complete' && pageLoaded === false) {
      onPageLoad();
      setPageLoaded(true);
    } else {
      window.addEventListener('load', onPageLoad);
      return () => window.removeEventListener('load', onPageLoad);
    }
  }, [pageLoaded]);

  useEffect(() => {
    setPlaying(sliderInView);
    if (!sliderInView) {
      restartTime();
    } else {
      setTime(100);
    }
  }, [sliderInView]);

  // slider reference object
  const sliderRef = useRef();
  const hasSlides = props?.module?.slide.length > 1;

  // react slick options - if speed is changed, animation time of clip path (in scss file) will need to be adjusted to match new time
  let settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    easing: 'ease-out',
    useCSS: false,
    swipe: false,
    afterChange: (index) => {
      if (isSwipe.current) {
        sliderRef.current.innerSlider.track.useCSS = false;
        sliderRef.current.innerSlider.props = Object.freeze({
          ...sliderRef.current.innerSlider.props,
          useCSS: false,
        });
        sliderRef.current.innerSlider.track.props = sliderRef.current.innerSlider.props;
        isSwipe.current = false;
      }

      const slideElement = sliderRef.current.innerSlider.list.querySelector(`[data-index="${index}"]`);
      const removeMobileUseMask = () => {
        setMobileUseMask(false);
        slideElement.removeEventListener('transitionend', removeMobileUseMask);
      };
      slideElement.addEventListener('transitionend', removeMobileUseMask);
      setPlaceholderSlide(index);
      // Not an elegant solution, but adding a delay to starting the timer
      // allowing re-renders to finish before resetting the timer, cases where
      // this happens:
      //  - Swipe on mobile
      //  - Manually changing slides
      //  - Timer is up
      setTimeout(() => {
        startTime(index);
      }, 100);
    },
    onSwipe() {
      restartTime();
      setPlaying(false);
      isSwipe.current = true;

      sliderRef.current.innerSlider.track.useCSS = true;
      sliderRef.current.innerSlider.props = Object.freeze({
        ...sliderRef.current.innerSlider.props,
        useCSS: true,
      });
      sliderRef.current.innerSlider.track.props = sliderRef.current.innerSlider.props;
    },
    responsive: [
      {
        breakpoint: 768,
        settings: {
          swipe: true,
        },
      },
    ],
  };

  return (
    <div className={classNames('category-hero-banner__outer-wrapper', {
      'category-hero-banner__outer-wrapper--mobile-use-mask': mobileUseMask,
    })}>
      {props?.breadcrumbs ? (
        <div className="category-hero-banner__breadcrumbs-wrapper">
          <SuperTemplateBreadcrumbs breadcrumbs={props.breadcrumbs} />
        </div>
      ) : null}

      <div className="category-hero-banner__wrapper">
        <div
          className={classNames('category-hero-banner__slider-wrapper', {
            'category-hero-banner__slider-wrapper--has-slides': hasSlides,
          })}
          onMouseMove={(e) => mouseMove(e)}
          onMouseLeave={() => setIsOffScreen(true)}
          ref={sliderIntersectRef}
        >
          {/* background image that remains during animation */}
          <SliderPlaceholderImage
            image={slide?.image}
            retinaImage={slide?.retinaImage}
            mobileImage={slide?.mobileImage}
            mobileRetinaImage={slide?.mobileRetinaImage}
            title={slide?.title}
            introContent={slide?.introContent}
            cta={slide?.cta}
          />
          {/* conditional render of slider if more that 1 image is present in slide prop */}

          {hasSlides ? (
            <Arrow
              style={{
                left: arrowX,
                top: arrowY,
              }}
              className={classNames('category-hero-banner__arrow', {
                'category-hero-banner__arrow-hidden': isOffScreen,
                'category-hero-banner__arrow-right': rightArrow,
              })}
            />
          ) : null}

          {hasSlides ? (
            <Slider {...settings} ref={sliderRef}>
              {props?.module?.slide.map((singleSlide, i) => (
                <div
                  key={i}
                  className="category-hero-banner__slide-container"
                  onClick={slideClick}
                  onKeyUp={() => undefined}
                  role="button"
                  tabIndex={-1}
                >
                  <div>
                    <img
                      className="category-hero-banner__desktop-image"
                      src={singleSlide.desktopImage?.mediaItemUrl}
                      srcSet={`${singleSlide.desktopImage?.mediaItemUrl}, ${
                        singleSlide.desktopRetinaImage
                          ? singleSlide.desktopRetinaImage?.mediaItemUrl + ' 2x'
                          : ''
                      }`}
                      alt={singleSlide.desktopImage?.altText}
                      />
                    <img
                      className="category-hero-banner__mobile-image"
                      src={singleSlide.mobileImage?.mediaItemUrl}
                      srcSet={`${singleSlide.mobileImage?.mediaItemUrl}, ${
                        singleSlide.mobileRetinaImage
                          ? singleSlide.mobileRetinaImage?.mediaItemUrl + ' 2x'
                          : ''
                      }`}
                      alt={singleSlide.desktopImage?.altText}
                      />
                    <div className="category-hero-banner__overlay"></div>
                  </div>

                  <Container>
                    <SliderContent
                      title={singleSlide.title}
                      introContent={singleSlide.introContent}
                      cta={singleSlide.cta}
                      ctaAriaLabel={singleSlide.ctaAriaLabel}
                      setIsOffscreen={setIsOffScreen}
                    />
                  </Container>
                </div>
              ))}
            </Slider>
          ) : null}
        </div>

        {/* conditional render of desktop slider controls if more that 1 image is present in slide prop */}
        {hasSlides ? (
          <SliderControls
            slides={props?.module?.slide}
            slideIndex={slideIndex}
            gotoSlide={gotoSlide}
            playing={playing}
            time={time}
            autoswipetimer={props?.module.autoswipetimer}
            gotoNext={gotoNext}
            mobile={false}
            playState={playState}
            setPlayState={setPlayState}
          />
        ) : null}
      </div>
      {/* mobile content */}
      <SliderContent
        title={slide?.title}
        introContent={slide?.introContent}
        cta={slide?.cta}
        mobile={true}
      />

      {/* conditional render of mobile slider controls if more that 1 image is present in slide prop */}
      {hasSlides ? (
        <SliderControls
          slides={props?.module?.slide}
          slideIndex={slideIndex}
          gotoSlide={gotoSlide}
          playing={playing}
          time={time}
          autoswipetimer={props?.module.autoswipetimer}
          gotoNext={() => {
            setMobileUseMask(true);
            gotoNext();
          }}
          mobile={true}
          playState={playState}
          setPlayState={setPlayState}
        />
      ) : null}
      {/* quick links */}
      {props?.module?.showQuickLinks && props?.childPages?.edges ? (
        <div className="category-hero-banner__quick-links-wrapper">
          <SuperTemplateQuickLinks childPages={props?.childPages?.edges} />
        </div>
      ) : null}
    </div>
  );
};

export default CategoryHeroBanner;
